import React, {useEffect, useMemo, useRef, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {
  fetchCompanyAttendance,
  fetchControlDepartments,
} from '@/entities/attendance/api';
import {
  parsePeriodKey,
  periodFromPreset,
  periodKey,
  type Period,
  type PeriodPresetId,
} from '@/entities/attendance/period';
import {ALL_TIME, fetchLogsInRange, periodWindow} from '@/entities/log/day';
import {exportLogs, type LogFilters} from '@/entities/log/api';
import type {AccessLogRecord} from '@/entities/log/types';
import {fetchDevices} from '@/entities/device/api';
import {PersonAvatar} from '@/entities/personnel/ui/PersonAvatar';
import {buildCsv, reportFileName} from '@/features/view-attendance/lib/csv';
import {describePeriod, usePeriodLabel} from '@/features/view-attendance/lib/period-label';
import {PeriodPicker} from '@/features/view-attendance/ui/PeriodPicker';
import {ExportBar} from '@/features/view-attendance/ui/ExportBar';
import {
  eventLateness,
  indexLateness,
  lateMinutes,
  type EventLateness,
  type LatenessIndex,
} from '@/features/view-journal/lib/lateness';
import {DEFAULT_SORT, journalAccessors} from '@/features/view-journal/lib/sort';
import {sortParam, useTableSort} from '@/shared/lib/table-sort';
import {
  enumParam,
  intParam,
  stringParam,
  useParamState,
  type ParamCodec,
} from '@/shared/lib/use-param-state';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {formatCount, formatDate} from '@/shared/lib/format';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {HelpNote} from '@/shared/ui/explain';
import {useContentInsets} from '@/shared/ui/screen';
import {
  FilterPopover,
  FilterSelect,
  type FilterOption,
} from '@/shared/ui/filter-bar';
import {Sheet} from '@/shared/ui/sheet';
import {SkeletonList} from '@/shared/ui/skeleton';
import {DataList} from '@/shared/ui/data-list';
import {StatusPill} from '@/shared/ui/status';
import {Cell, NameCell, type Column} from '@/shared/ui/table';
import {AppText} from '@/shared/ui/text';
import {fonts} from '@/shared/ui/fonts';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * «Журнал» — все проходы компании ТАБЛИЦЕЙ, с видимыми фильтрами и сортировкой по
 * столбцам.
 *
 * ## Что это заменило
 *
 * Ленту карточек. Карточка — это то, на что СМОТРЯТ: лицо, имя, пилюля, 76pt высоты с
 * воздухом вокруг. На мониторе это восемь событий на экран там, где таблица показывает
 * сорок, и — главное — ни одно поле не стоит в столбце, то есть их нельзя сравнить
 * сверху вниз и нельзя упорядочить. Человек, которому нужно «все отказы за вчера,
 * начиная с худшего совпадения», выгружал XLSX и открывал его в Excel. Именно про это
 * и был запрос: то, что скачивают, надо видеть в приложении.
 *
 * Телефон карточки сохраняет: таблица в 390pt — это горизонтальная прокрутка, а
 * горизонтальная прокрутка — это потерянный читатель. Сортировка там есть тоже, просто
 * не заголовками, а строкой «Сначала:».
 *
 * ## Почему журнал ВСЕГДА за период
 *
 * Ни фильтра по датам, ни сортировки у `GET /control/logs` нет — только фильтры,
 * `limit` и `offset` (см. `entities/log/api.ts`). Отсортировать одну серверную
 * страницу и нарисовать стрелку в заголовке значит соврать: «самое раннее» окажется
 * самым ранним из тридцати видимых, а не за день. Поэтому период собирается целиком
 * ({@link fetchLogsInRange}), сортировка и разбивка на страницы — по собранному, и
 * три числа наверху считаются по нему же.
 *
 * У сбора есть потолок (50 страниц по 100 строк). Упёрлись — `complete: false`, и об
 * этом написано строкой над таблицей, а не умолчано: список, оборвавшийся на середине
 * месяца, читается как «больше ничего не было».
 *
 * ## Выгрузка
 *
 * Кнопка «Скачать» отдаёт РОВНО то, что на экране: те же строки, в том же порядке, с
 * теми же столбцами. Серверная выгрузка (вся история по фильтрам, без периода) осталась
 * второй кнопкой и подписана тем, чем она отличается, — см. `ExportBar`.
 */
type Mode = 'all' | 'granted' | 'denied';
type Lateness = 'all' | 'late' | 'onTime';

/* Кодеки адреса. Модульные константы, а не литералы на месте вызова: ссылка на них
   должна быть стабильной, иначе разбор пересчитывался бы на каждый рендер. */
const RESULT_PARAM = enumParam<Mode>(['all', 'granted', 'denied'], 'all');
const LATENESS_PARAM = enumParam<Lateness>(['all', 'late', 'onTime'], 'all');

/** Пресеты периода, которые журнал предлагает первой строкой. */
const PRESETS: readonly PeriodPresetId[] = ['today', 'yesterday', 'week', 'month'];

/** Сколько строк на странице таблицы. */
const PER_PAGE = 50;
const PAGE_PARAM = intParam(1);
const PER_PAGE_PARAM = intParam(PER_PAGE);
const SORT_PARAM = sortParam(DEFAULT_SORT);
const PAGE_SIZES = [50, 100, 200] as const;

/** Порядки, предлагаемые чипами на узком окне, и подписи к ним. */
const PHONE_SORTS = ['time', 'person', 'device', 'result'] as const;
const PHONE_SORT_LABELS: Record<string, string> = {
  time: 'journal.colTime',
  person: 'journal.colPerson',
  device: 'journal.colDevice',
  result: 'journal.colResult',
};
const SEARCH_DEBOUNCE_MS = 300;

const styles = StyleSheet.create({
  /* Десктоп: фильтры сверху, панель с таблицей забирает остаток окна. */
  deskFrame: {flex: 1, minHeight: 0, gap: spacing(3), paddingBottom: spacing(4)},
  deskScroll: {padding: spacing(4)},
  /* Период и счётчики — строкой под рейкой страниц. */
  footRow: {flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: spacing(3)},
  foot: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},

  /* Телефон: те же фильтры блоком над лентой карточек. */
  list: {gap: spacing(2), paddingBottom: spacing(6), flexGrow: 1},
  listEmpty: {justifyContent: 'center'},
  head: {gap: spacing(3), paddingTop: spacing(1), paddingBottom: spacing(3)},

  counts: {flexDirection: 'row', gap: spacing(3), flexWrap: 'wrap'},
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: spacing(3),
    flexWrap: 'wrap',
  },
  count: {gap: 2},

  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  rail: {width: 3, alignSelf: 'stretch', borderRadius: radii.pill},
  when: {width: 86, gap: 1},
  clock: {fontFamily: fonts.mono.medium, fontSize: 13, lineHeight: 18},
  body: {flex: 1, gap: 1},
  meta: {alignItems: 'flex-end', gap: 2, maxWidth: '38%'},
  scope: {gap: spacing(2), alignItems: 'flex-start'},
  sheetBody: {gap: spacing(4)},
});

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Секунды в журнале не роскошь: два прохода в одну минуту — обычное дело. */
function clock(millis: number): string {
  const d = new Date(millis);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

/** Значение, которое устоялось, — только оно ходит в сеть. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return settled;
}

/** Одно событие карточкой — телефонная форма той же строки таблицы. */
const JournalRow = React.memo(function JournalRow({
  companyId,
  log,
  lateness,
}: {
  companyId: string;
  log: AccessLogRecord;
  /** Вердикт по графику. `none` — сказать нечего, и строка молчит. */
  lateness: EventLateness;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const name = log.person_name || t('home.feed.unknownPerson');

  return (
    <Card padding={3} testID={`journal-row-${log.event_id}`}>
      <View style={styles.row}>
        <View
          style={[
            styles.rail,
            {backgroundColor: log.granted ? p.signal : p.destructive},
          ]}
        />
        <View style={styles.when}>
          <AppText style={styles.clock}>{clock(log.created_at_millis)}</AppText>
          <AppText variant="micro">{formatDate(log.created_at_millis)}</AppText>
        </View>
        <PersonAvatar
          companyId={companyId}
          recordId={log.granted ? log.record_id : null}
          name={name}
          size={36}
        />
        <View style={styles.body}>
          <AppText variant="subtitle" numberOfLines={1}>
            {name}
          </AppText>
          <AppText variant="micro" numberOfLines={1}>
            {[log.device_name, log.department_name].filter(Boolean).join(' · ')}
          </AppText>
        </View>
        <View style={styles.meta}>
          <StatusPill
            tone={log.granted ? 'granted' : 'denied'}
            size="sm"
            dot={false}
            label={t(log.granted ? 'home.feed.granted' : 'home.feed.denied')}
          />
          {lateness.kind === 'late' ? (
            <AppText variant="micro" color={p.warningInk}>
              {t('journal.lateBy', {minutes: lateness.minutes})}
            </AppText>
          ) : lateness.kind === 'onTime' ? (
            <AppText variant="micro" color={p.signalInk}>
              {t('journal.onTime')}
            </AppText>
          ) : null}
        </View>
      </View>
    </Card>
  );
});

export function Journal({
  companyId,
  scope,
  onScopeCleared,
  exportOpen,
  onExportOpenChange,
}: {
  companyId: string;
  /**
   * Лист выгрузки — контролируемый снаружи, когда экран держит кнопку «Скачать» в
   * своей шапке (телефон); без этих пропов журнал открывает его сам с кнопки в
   * полосе над таблицей (широкое окно).
   */
  exportOpen?: boolean;
  onExportOpenChange?: (open: boolean) => void;
  /**
   * Журнал ОДНОГО человека — не фильтр формы, а параметр перехода: «показать этого
   * сотрудника в журнале» приходит из отчёта вместе с ним. Снимается одним нажатием,
   * и пока он в силе, об этом написано баннером, а не одной подсвеченной фишкой:
   * пустой журнал, суженный до человека, о котором забыли, — это загадка.
   */
  scope?: {recordId: string; name?: string; period?: Period};
  /**
   * Область сняли. Экран обязан вычистить параметры маршрута: на вебе они лежат в
   * АДРЕСЕ, и без этого перезагрузка страницы вернула бы снятую область обратно, а
   * ссылкой на «журнал компании» человек поделился бы журналом одного сотрудника.
   */
  onScopeCleared?: () => void;
}) {
  const {t, i18n} = useTranslation();
  const p = useTheme();
  const insets = useContentInsets();
  const {isDesktop} = useBreakpoint();
  const {label: periodTitle} = usePeriodLabel();

  // «Сегодня» закрепляется на монтирование: список, переехавший на новый день посреди
  // взгляда, врёт про «сегодня» до следующего обновления.
  const now = useMemo(() => new Date(), []);

  /*
    Отбор журнала живёт В АДРЕСЕ, а не в состоянии компонента: это вопрос, который
    человек задал («кто опаздывал в цехе на прошлой неделе»), и он обязан пережить F5,
    вернуться по «назад» и уехать ссылкой коллеге. См. `shared/lib/use-param-state`.

    В компоненте остаётся только ЖЕСТ: открыт ли редактор произвольного отрезка и
    крутится ли выгрузка. Им в ссылке делать нечего.
  */
  const defaultPeriod = useMemo(() => scope?.period ?? periodFromPreset('today', now), [scope, now]);
  /** `null` — «за всё время»; всё остальное — конкретный отрезок календаря. */
  const periodCodec = useMemo<ParamCodec<Period | null>>(() => {
    const empty = periodKey(defaultPeriod);
    return {
      parse: raw => (raw === 'all' ? null : parsePeriodKey(raw, now)),
      format: value => {
        if (value === null) {
          return 'all';
        }
        const key = periodKey(value);
        return key === empty ? undefined : key;
      },
    };
  }, [defaultPeriod, now]);
  const [period, setPeriod] = useParamState<Period | null>('period', defaultPeriod, periodCodec);
  const [recordId, setRecordId] = useState<string | undefined>(scope?.recordId);
  const [custom, setCustom] = useState(false);
  const [mode, setMode] = useParamState<Mode>('result', 'all', RESULT_PARAM);
  const [deviceId, setDeviceId] = useParamState('device', '', stringParam);
  const [departmentId, setDepartmentId] = useParamState('department', '', stringParam);
  /** `all` — не сужать; `late` — только опоздания; `onTime` — только пришедшие вовремя. */
  const [lateness, setLateness] = useParamState('late', 'all' as Lateness, LATENESS_PARAM);
  const [search, setSearch] = useParamState('q', '', stringParam);
  const [page, setPage] = useParamState('page', 1, PAGE_PARAM);
  const [perPage, setPerPage] = useParamState('per', PER_PAGE, PER_PAGE_PARAM);
  const [ownExporting, setOwnExporting] = useState(false);
  const exporting = exportOpen ?? ownExporting;
  const setExporting = onExportOpenChange ?? setOwnExporting;

  const debounced = useDebounced(search.trim(), SEARCH_DEBOUNCE_MS);

  /** Фильтры, которые применяет СЕРВЕР. Период и сортировка ему не по зубам. */
  const filters: LogFilters = {
    result: mode === 'all' ? undefined : mode,
    recordId,
    deviceId: deviceId || undefined,
    departmentId: departmentId || undefined,
    search: debounced || undefined,
  };

  // Новый отбор — новый список, и седьмая страница старого к нему отношения не имеет.
  const filterKey = [
    period ? periodKey(period) : 'all',
    recordId ?? '',
    mode,
    deviceId,
    departmentId,
    debounced,
  ].join('|');
  /*
    Первый прогон пропускается намеренно. Страница теперь живёт в адресе, и сброс на
    монтировании затирал бы `?page=3` из присланной ссылки ровно в тот момент, когда её
    открывают.
  */
  const firstFilterRun = useRef(true);
  useEffect(() => {
    if (firstFilterRun.current) {
      firstFilterRun.current = false;
      return;
    }
    setPage(1);
    // `setPage` стабилен по построению хука; сторожить его значит гонять эффект зря.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, lateness, perPage]);

  const devices = useQuery({
    queryKey: ['devices', companyId, 'all'],
    queryFn: () => fetchDevices(companyId, {perPage: 100}),
  });

  const departments = useQuery({
    queryKey: ['control-departments', companyId],
    queryFn: () => fetchControlDepartments(companyId),
  });

  /**
   * Явка за тот же период — единственный источник вердикта «опоздал».
   *
   * `include_days` дорог (двести человек за месяц — шесть тысяч строк), поэтому он
   * просится только когда период задан: «за всё время» отчёт явки не считает вовсе, у
   * него потолок в 366 дней. Фильтр по отделу передаётся дальше — незачем считать
   * явку всей компании, когда на экране один отдел.
   *
   * Ошибка здесь не ломает журнал: у отчёта явки своё право, и `403` значит ровно
   * «опозданий не покажем», а не «журнала не будет».
   */
  const attendance = useQuery({
    queryKey: [
      'journal-attendance',
      companyId,
      period ? periodKey(period) : 'all',
      departmentId,
    ],
    queryFn: () =>
      fetchCompanyAttendance(companyId, period as Period, {
        includeDays: true,
        departmentIds: departmentId ? [departmentId] : undefined,
      }),
    enabled: period !== null,
    retry: false,
  });

  /** Можно ли вообще говорить об опозданиях на этом экране. */
  const lateReady = period !== null && attendance.isSuccess;
  const lateIndex: LatenessIndex = useMemo(
    () => indexLateness(attendance.data?.days ?? []),
    [attendance.data],
  );
  const lateOf = useMemo(
    () => (row: AccessLogRecord) => lateMinutes(lateIndex, row),
    [lateIndex],
  );

  // Опоздания есть не всегда: «за всё время» их не считает никто, а отчёт явки может
  // и не отдаться по правам. Фильтр, который нечем применить, обязан сняться сам —
  // иначе экран молча пустеет.
  useEffect(() => {
    if (!lateReady) {
      setLateness('all');
    }
    // `setLateness` стабилен по построению `useParamState`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lateReady]);

  const query = useQuery({
    queryKey: ['journal', companyId, filterKey],
    queryFn: () =>
      fetchLogsInRange(companyId, filters, period ? periodWindow(period) : ALL_TIME),
  });

  // Пустой массив — свой на каждый рендер, поэтому запасное значение считается
  // ОТДЕЛЬНОЙ памяткой: иначе список пересортировывался бы на каждый рендер, пока
  // запрос в пути.
  const collected = useMemo(() => query.data?.events ?? [], [query.data]);
  /**
   * Отбор по опозданию — на клиенте и ПОСЛЕ сбора: сервер журнала о графике не знает.
   * «Только опоздания» оставляет ровно события ПРИХОДА опоздавших дней, а не все
   * проходы этих людей за день, — иначе один опоздавший даёт шесть строк.
   */
  const filtered = useMemo(
    () =>
      lateness === 'all'
        ? collected
        : collected.filter(e => eventLateness(lateIndex, e).kind === lateness),
    [collected, lateness, lateIndex],
  );
  /*
    Строки приходят из `collectDay` по возрастанию времени, а журнал читают наоборот.
    Переворот здесь — не украшение: общая сортировка устойчива по ИСХОДНОМУ номеру
    строки, значит порядок «новые вперёд» сохраняется и внутри равных значений любого
    другого столбца. Без него два прохода на одном терминале вставали бы старым вверх.
  */
  const newestFirst = useMemo(() => [...filtered].reverse(), [filtered]);
  const accessors = useMemo(() => journalAccessors(lateOf), [lateOf]);
  const [sortState, setSortState] = useParamState('sort', DEFAULT_SORT, SORT_PARAM);
  const sorted = useTableSort(newestFirst, accessors, DEFAULT_SORT, undefined, {
    value: sortState,
    onChange: setSortState,
  });
  const sort = sorted.state;
  const rows = sorted.rows;

  // Новый порядок — новая первая страница: седьмая страница прежнего к нему отношения
  // не имеет. Первый прогон снова пропускаем — по той же причине, что и выше.
  const firstSortRun = useRef(true);
  useEffect(() => {
    if (firstSortRun.current) {
      firstSortRun.current = false;
      return;
    }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);
  const granted = filtered.filter(e => e.granted).length;
  const total = rows.length;
  // Страницу режет сам список; здесь остаются только числа для подвала и выгрузки.

  const periodLabel = period
    ? periodTitle(describePeriod(period, now))
    : t('journal.allTime');

  const deviceOptions: FilterOption[] = [
    {value: '', label: t('actions.any')},
    ...(devices.data?.data ?? []).map(d => ({value: d.device_id, label: d.device_name})),
  ];

  const departmentOptions: FilterOption[] = [
    {value: '', label: t('actions.any')},
    ...(departments.data?.departments ?? []).map(d => ({
      value: d.department_id,
      label: d.name,
    })),
  ];

  const presetOptions: FilterOption[] = [
    ...PRESETS.map(id => ({value: id as string, label: t(`reports.preset.${id}`)})),
    {value: 'all', label: t('journal.allTime')},
    {value: 'custom', label: t('journal.custom')},
  ];

  /**
   * Какая фишка периода горит сейчас.
   *
   * «Свой период» — это не только нажатая фишка, но и ЛЮБОЙ отрезок, не совпавший ни с
   * одним пресетом: журнал одного человека открывается из отчёта за произвольный месяц,
   * и подсветить там «Сегодня» значило бы соврать про то, что на экране.
   */
  const matchedPreset =
    period === null
      ? null
      : PRESETS.find(id => periodKey(periodFromPreset(id, now)) === periodKey(period)) ??
        null;
  const activePreset =
    period === null ? 'all' : custom || !matchedPreset ? 'custom' : matchedPreset;

  const pickPreset = (value: string) => {
    if (value === 'all') {
      setCustom(false);
      setPeriod(null);
      return;
    }
    if (value === 'custom') {
      setCustom(true);
      setPeriod(period ?? periodFromPreset('week', now));
      return;
    }
    setCustom(false);
    setPeriod(periodFromPreset(value as PeriodPresetId, now));
  };

  const dirty =
    !!recordId ||
    mode !== 'all' ||
    !!deviceId ||
    !!departmentId ||
    !!search.trim() ||
    lateness !== 'all';

  const reset = () => {
    setRecordId(undefined);
    onScopeCleared?.();
    setMode('all');
    setDeviceId('');
    setDepartmentId('');
    setLateness('all');
    setSearch('');
  };

  /** CSV ровно того, что на экране: те же строки, тот же порядок, те же столбцы. */
  const visibleCsv = () =>
    buildCsv(
      [
        t('reports.csv.date'),
        t('journal.colTime'),
        t('journal.colPerson'),
        t('journal.colNumber'),
        t('journal.colDevice'),
        t('journal.colDepartment'),
        t('journal.colResult'),
        t('journal.colLate'),
        t('journal.colMessage'),
      ],
      rows.map(e => [
        formatDate(e.created_at_millis),
        clock(e.created_at_millis),
        e.person_name,
        e.employee_number ?? '',
        e.device_name,
        e.department_name ?? '',
        t(e.granted ? 'home.feed.granted' : 'home.feed.denied'),
        lateOf(e) ?? '',
        e.message,
      ]),
    );

  const fileName = reportFileName([t('journal.fileName'), periodLabel], '').slice(0, -1);

  /**
   * Панель фильтров: одна строка пилюль «Что: чем».
   *
   * Раньше это была форма — подпись слева, группа фишек справа, по ряду на фильтр, —
   * и пять фильтров занимали над таблицей три ряда. Все варианты были нарисованы
   * одновременно, хотя выбирают из них один и меняют раз в сеанс.
   *
   * Сжатие не стоило ничего важного: на кнопке по-прежнему написано И что фильтруется,
   * И чем, — то есть ответ на «почему тут пусто» виден, не открывая ничего. Крашеная
   * пилюля означает «этот фильтр сейчас что-то прячет».
   */
  const filterPills = (
    <>
      <FilterSelect
        label={t('journal.period')}
        value={activePreset}
        options={presetOptions}
        onChange={pickPreset}
        neutral="all"
        phone={!isDesktop}
        testID="journal-period"
      />

      {/* Свои даты — за той же пилюлей, что и пресеты: два поля календаря, открытые
          в панели постоянно, это и есть та форма, от которой панель уходит. */}
      {activePreset === 'custom' && period ? (
        <FilterPopover
          label={t('journal.dates')}
          value={periodLabel}
          active
          phone={!isDesktop}
          minWidth={320}
          testID="journal-dates">
          <PeriodPicker period={period} onChange={setPeriod} now={now} />
        </FilterPopover>
      ) : null}

      <FilterSelect
        label={t('journal.result')}
        value={mode}
        options={[
          {value: 'all', label: t('actions.any')},
          {value: 'granted', label: t('home.feed.granted')},
          {value: 'denied', label: t('home.feed.denied')},
        ]}
        onChange={next => setMode(next as Mode)}
        neutral="all"
        phone={!isDesktop}
        testID="journal-result"
      />

      <FilterSelect
        label={t('journal.colDevice')}
        value={deviceId}
        options={deviceOptions}
        onChange={setDeviceId}
        phone={!isDesktop}
        testID="journal-device"
      />

      <FilterSelect
        label={t('journal.colDepartment')}
        value={departmentId}
        options={departmentOptions}
        onChange={setDepartmentId}
        phone={!isDesktop}
        testID="journal-department"
      />

      {/*
        Опоздания есть только там, где задан график, и только за конкретный период:
        «за всё время» отчёт явки не считает вовсе (у него потолок в 366 дней), а само
        право на отчёт может и не быть выдано. Нечем ответить — пилюли нет, и это
        честнее контрола, который на нажатие отвечает пустотой.
      */}
      {lateReady ? (
        <FilterSelect
          label={t('journal.lateness')}
          value={lateness}
          options={[
            {value: 'all', label: t('actions.any')},
            {value: 'late', label: t('journal.latenessLate')},
            {value: 'onTime', label: t('journal.latenessOnTime')},
          ]}
          onChange={next => setLateness(next as 'all' | 'late' | 'onTime')}
          neutral="all"
          phone={!isDesktop}
          testID="journal-lateness"
        />
      ) : null}
    </>
  );


  /** Журнал сужен до одного человека — это не фильтр формы, а параметр перехода. */
  const scopeBanner = recordId ? (
    <View style={styles.scope}>
      <Banner
        tone="info"
        title={t('journal.scopedTo', {
          name: scope?.name ?? t('home.feed.unknownPerson'),
        })}
        message={t('journal.scopedToHint')}
      />
      <AppButton
        title={t('journal.scopeClear')}
        tone="outline"
        size="sm"
        onPress={() => {
          setRecordId(undefined);
          onScopeCleared?.();
        }}
        testID="journal-scope-clear"
      />
    </View>
  ) : null;

  /** Три числа: сколько собрано, сколько пропущено, сколько отказано. */
  const counts = (
    <View style={styles.counts} testID="journal-counts">
      <AppText variant="caption" color={p.signalInk}>
        {t('home.feed.granted')}: {formatCount(granted)}
      </AppText>
      <AppText
        variant="caption"
        color={total - granted > 0 ? p.destructiveInk : undefined}>
        {t('home.feed.denied')}: {formatCount(total - granted)}
      </AppText>
    </View>
  );

  /** Просмотр упёрся в потолок — сказать это, а не оборвать список молча. */
  const partial =
    query.data && !query.data.complete ? (
      <HelpNote text={t('journal.partial', {scanned: query.data.scanned})} />
    ) : null;

  const exportSheet = (
    <Sheet
      visible={exporting}
      onClose={() => setExporting(false)}
      title={t('journal.download')}
      testID="journal-export">
      <View style={styles.sheetBody}>
        <ExportBar
          fileName={fileName}
          contents={t('journal.downloadContents', {period: periodLabel, count: total})}
          note={t('journal.downloadDiffers')}
          onServer={format => exportLogs(companyId, filters, format, i18n.language)}
          onLocal={visibleCsv}
        />
      </View>
    </Sheet>
  );

  /*
    Пустой период — не тупик. Выход отсюда — один: снять то, что сузило список, и
    заглушка обязана его предлагать сама. Отбор снимается «Сбросить»; чистый журнал,
    в котором за сегодня ещё никто не прошёл (обычное утро), — «За всё время».
  */
  const empty = query.isError ? (
    <EmptyState
      tone="error"
      title={t('journal.error')}
      actionLabel={t('actions.retry')}
      onAction={() => void query.refetch()}
    />
  ) : dirty ? (
    <EmptyState
      icon="logs"
      title={t(lateness !== 'all' ? 'journal.emptyLate' : 'journal.emptyFiltered')}
      actionLabel={t('actions.reset')}
      onAction={reset}
    />
  ) : (
    <EmptyState
      icon="logs"
      title={t('journal.empty')}
      actionLabel={period === null ? undefined : t('journal.allTime')}
      onAction={period === null ? undefined : () => pickPreset('all')}
    />
  );

  const columns: Column<AccessLogRecord>[] = [
    {
      key: 'time',
      title: t('journal.colTime'),
      width: 148,
      sortKey: 'time',
      render: r => (
        <Cell mono>{`${formatDate(r.created_at_millis).slice(0, 5)} ${clock(
          r.created_at_millis,
        )}`}</Cell>
      ),
    },
    {
      key: 'person',
      title: t('journal.colPerson'),
      flex: 2,
      sortKey: 'person',
      render: r => (
        <NameCell
          mark={
            <PersonAvatar
              companyId={companyId}
              recordId={r.granted ? r.record_id : null}
              name={r.person_name || t('home.feed.unknownPerson')}
              size={26}
            />
          }>
          {r.person_name || t('home.feed.unknownPerson')}
        </NameCell>
      ),
    },
    {
      key: 'number',
      title: t('journal.colNumber'),
      width: 104,
      minWidth: 1400,
      sortKey: 'number',
      render: r => <Cell mono tone={p.mutedForeground}>{r.employee_number || '—'}</Cell>,
    },
    {
      key: 'department',
      title: t('journal.colDepartment'),
      flex: 1.2,
      minWidth: 1180,
      sortKey: 'department',
      render: r => <Cell tone={p.mutedForeground}>{r.department_name || '—'}</Cell>,
    },
    {
      key: 'device',
      title: t('journal.colDevice'),
      flex: 1.4,
      sortKey: 'device',
      render: r => <Cell tone={p.mutedForeground}>{r.device_name}</Cell>,
    },
    {
      key: 'result',
      title: t('journal.colResult'),
      width: 116,
      sortKey: 'result',
      render: r => (
        <StatusPill
          tone={r.granted ? 'granted' : 'denied'}
          size="sm"
          dot={false}
          label={t(r.granted ? 'home.feed.granted' : 'home.feed.denied')}
        />
      ),
    },
    // Столбец опозданий есть, только когда есть чем его заполнить: без периода и
    // без права на отчёт явки он был бы колонкой прочерков.
    ...(lateReady
      ? [
          {
            key: 'late',
            title: t('journal.colLate'),
            width: 116,
            align: 'right' as const,
            sortKey: 'late',
            render: (r: AccessLogRecord) => {
              const verdict = eventLateness(lateIndex, r);
              if (verdict.kind === 'late') {
                // Без `mono`: в клетке не число, а фраза («+14 мин»), и моноширинная
                // «мин» разъезжается буквами. Выравнивать по цифрам тут нечего —
                // соседние клетки это «вовремя» и прочерк.
                return (
                  <Cell tone={p.warningInk}>
                    {t('journal.lateBy', {minutes: verdict.minutes})}
                  </Cell>
                );
              }
              if (verdict.kind === 'onTime') {
                return <Cell tone={p.signalInk}>{t('journal.onTime')}</Cell>;
              }
              return <Cell tone={p.mutedForeground}>—</Cell>;
            },
          },
        ]
      : []),
    {
      key: 'message',
      title: t('journal.colMessage'),
      flex: 1.6,
      // Последний столбец, который уходит первым: причина отказа полезна, но
      // никогда не важнее того, кто и когда прошёл.
      minWidth: 1560,
      render: r => <Cell tone={p.mutedForeground}>{r.message || '—'}</Cell>,
    },
  ];

  /*
    Тот же список, что на «Явке».

    Раньше журнал жил в пристыкованной панели со своим тулбаром, своим подвалом и
    своей рейкой, а на телефоне — лентой с отдельной шапкой, где те же фильтры
    выкладывались заново. Теперь устройство одно (`shared/ui/data-list`): здесь
    остаётся только то, что у журнала своё, — чем фильтровать, как выглядит столбец и
    как выглядит карточка.
  */
  const list = (
    <DataList<AccessLogRecord>
      testID="journal"
      rows={newestFirst}
      /*
        Сколько строк ленты прочитано, чтобы собрать период. Без этого «сегодня
        ещё никто не прошёл» выглядело бы как «данных нет вовсе»: список пуст ДО
        отбора, и полоса с чипом периода не рисуется — а именно она и нужна, чтобы
        выйти из пустого утра. Ноль здесь — только когда сервер не отдал ни строки.
      */
      total={query.data?.scanned}
      columns={columns}
      keyOf={r => r.event_id}
      accessors={accessors}
      initialSort={DEFAULT_SORT}
      sortControl={{value: sortState, onChange: setSortState}}
      pageControl={{value: page, onChange: setPage}}
      perPageControl={{value: perPage, onChange: setPerPage}}
      perPageOptions={PAGE_SIZES}
      actions={
        <AppButton
          testID="journal-download"
          icon="download"
          tone="outline"
          size="md"
          title={t('journal.downloadVisible')}
          onPress={() => setExporting(true)}
        />
      }
      search={{value: search, onChange: setSearch, placeholder: t('journal.search')}}
      filters={filterPills}
      dirty={dirty}
      onReset={reset}
      loading={query.isPending}
      skeleton={<SkeletonList count={10} avatar={false} />}
      empty={empty}
      noMatches={empty}
      phoneSorts={PHONE_SORTS}
      sortLabel={key => t(PHONE_SORT_LABELS[key] ?? key)}
      renderCard={row => (
        <JournalRow
          companyId={companyId}
          log={row}
          lateness={eventLateness(lateIndex, row)}
        />
      )}
      // Три числа журнала — тот же блок, что стоял в подвале панели: сколько
      // собрано, сколько пропущено, сколько отказано.
      footer={counts}
    />
  );

  return (
    <View style={[styles.deskFrame, insets]}>
      {scopeBanner}
      {partial}
      {list}
      {exportSheet}
    </View>
  );
}
